// src/components/songEdit/versions/AddVersionModal.tsx
import React, { useState, useEffect } from 'react';
import { SongVersion } from './VersionsList';
import './AddVersionModal.css';

interface AddVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (versionData: VersionFormData) => Promise<void>;
  songId: number;
  existingVersion?: SongVersion | null;
}

export interface VersionFormData {
  version_title: string;
  alt_title?: string;
  arranger_id?: number;
  arranger_name?: string;
  arrangement_type?: string;
  performance_type?: string;
  key_signature?: string;
  verses_included?: string;
  video_link?: string;
  lyrics?: string;
}

const PERFORMANCE_TYPES = [
  { value: 'CONG', label: 'Congregation' },
  { value: 'CHOIR', label: 'Choir' },
  { value: 'GOSPEL', label: 'Gospel Choir' },
  { value: 'CANTOR', label: 'Cantor' },
  { value: 'SOLOIST', label: 'Soloist' },
  { value: 'INSTRUMENTAL', label: 'Instrumental' },
  { value: 'MIXED', label: 'Mixed' }
];

// 12 Major keys + 12 Minor keys for type-ahead suggestions
const KEY_SUGGESTIONS = [
  'C Major', 'G Major', 'D Major', 'A Major', 'E Major', 'B Major',
  'F Major', 'Bb Major', 'Eb Major', 'Ab Major', 'Db Major', 'F# Major',
  'A Minor', 'E Minor', 'B Minor', 'F# Minor', 'C# Minor', 'G# Minor',
  'D Minor', 'G Minor', 'C Minor', 'F Minor', 'Bb Minor', 'Eb Minor'
];

export const AddVersionModal: React.FC<AddVersionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  songId,
  existingVersion
}) => {
  const [formData, setFormData] = useState<VersionFormData>({
    version_title: '',
    alt_title: '',
    arrangement_type: '',
    performance_type: '',
    key_signature: '',
    verses_included: '',
    video_link: '',
    lyrics: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing version data when editing
  useEffect(() => {
    if (existingVersion) {
      setFormData({
        version_title: existingVersion.version_title || '',
        alt_title: existingVersion.alt_title || '',
        arranger_name: existingVersion.arranger_name || '',
        arrangement_type: existingVersion.arrangement_type || '',
        performance_type: existingVersion.performance_type || '',
        key_signature: existingVersion.key_signature || '',
        verses_included: existingVersion.verses_included || '',
        video_link: existingVersion.video_link || '',
        lyrics: ''
      });
    } else {
      // Reset form for new version
      setFormData({
        version_title: '',
        alt_title: '',
        arrangement_type: '',
        performance_type: '',
        key_signature: '',
        verses_included: '',
        video_link: '',
        lyrics: ''
      });
    }
    setError(null);
  }, [existingVersion, isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      await onSave(formData);
      handleClose();
    } catch (err: any) {
      console.error('[AddVersionModal] Save failed:', err);
      setError(err.response?.data?.message || 'Failed to save version');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      version_title: '',
      alt_title: '',
      arrangement_type: '',
      performance_type: '',
      key_signature: '',
      verses_included: '',
      video_link: '',
      lyrics: ''
    });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const isEditMode = !!existingVersion;

  return (
    <div className="modal d-block version-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditMode ? 'Edit Version' : 'Add New Version'}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={saving}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {/* Version Title */}
              <div className="mb-3">
                <label htmlFor="version_title" className="form-label">
                  Version Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="version_title"
                  name="version_title"
                  value={formData.version_title}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Mass 11 - Orbis factor"
                />
                <small className="form-text text-muted">
                  Descriptive title for this version/arrangement
                </small>
              </div>

              {/* Alternative Title */}
              <div className="mb-3">
                <label htmlFor="alt_title" className="form-label">
                  Alternative Title
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="alt_title"
                  name="alt_title"
                  value={formData.alt_title}
                  onChange={handleChange}
                  placeholder="Optional alternative name"
                />
              </div>

              {/* Arranger Name */}
              <div className="mb-3">
                <label htmlFor="arranger_name" className="form-label">
                  Arranger Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="arranger_name"
                  name="arranger_name"
                  value={formData.arranger_name || ''}
                  onChange={handleChange}
                  placeholder="e.g., David Hurd"
                />
                <small className="form-text text-muted">
                  Person who arranged this version
                </small>
              </div>

              {/* Row: Performance Type and Key Signature */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="performance_type" className="form-label">
                    Performance Type
                  </label>
                  <select
                    className="form-select"
                    id="performance_type"
                    name="performance_type"
                    value={formData.performance_type}
                    onChange={handleChange}
                  >
                    <option value="">Select type...</option>
                    {PERFORMANCE_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="key_signature" className="form-label">
                    Key Signature
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="key_signature"
                    name="key_signature"
                    list="key_suggestions"
                    value={formData.key_signature}
                    onChange={handleChange}
                    placeholder="Type to search keys..."
                    autoComplete="off"
                  />
                  <datalist id="key_suggestions">
                    {KEY_SUGGESTIONS.map((key) => (
                      <option key={key} value={key} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Arrangement Type */}
              <div className="mb-3">
                <label htmlFor="arrangement_type" className="form-label">
                  Arrangement Type
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="arrangement_type"
                  name="arrangement_type"
                  value={formData.arrangement_type}
                  onChange={handleChange}
                  placeholder="e.g., Plainsong Mode 1, SATB, Organ"
                />
                <small className="form-text text-muted">
                  Style or instrumentation of this arrangement
                </small>
              </div>

              {/* Verses Included */}
              <div className="mb-3">
                <label htmlFor="verses_included" className="form-label">
                  Verses Included
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="verses_included"
                  name="verses_included"
                  value={formData.verses_included}
                  onChange={handleChange}
                  placeholder="e.g., VS_1-4, VS_ALL, VS_1,3,5"
                />
                <small className="form-text text-muted">
                  Which verses are in this version
                </small>
              </div>

              {/* Video Link */}
              <div className="mb-3">
                <label htmlFor="video_link" className="form-label">
                  Video Link
                </label>
                <input
                  type="url"
                  className="form-control"
                  id="video_link"
                  name="video_link"
                  value={formData.video_link}
                  onChange={handleChange}
                  placeholder="https://youtube.com/watch?v=..."
                />
                <small className="form-text text-muted">
                  YouTube or other video URL for this version
                </small>
              </div>

              {/* Lyrics */}
              <div className="mb-3">
                <label htmlFor="lyrics" className="form-label">
                  Lyrics
                </label>
                <textarea
                  className="form-control"
                  id="lyrics"
                  name="lyrics"
                  value={formData.lyrics}
                  onChange={handleChange}
                  rows={6}
                  placeholder="Enter lyrics for this version (optional)"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !formData.version_title}
              >
                {saving ? 'Saving...' : isEditMode ? 'Update Version' : 'Create Version'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
